import type { SupportedLocale } from '../types'

type Messages = Record<string, any>

export const messages: Record<SupportedLocale, Messages> = {
  en: {
    contactForm: {
      headerTitle: 'Get in touch',
      headerTitleExpert: 'Get matched with a local expert',
      headerTitleSuccess: 'Thank you!',
      headerSubtitleStep1: 'Leave your email and we’ll reach out shortly.',
      headerSubtitleStep2: 'A few more details, then you’re done.',
      headerSubtitleExpert: 'Takes 30 seconds • No spam • Get relevant properties',
      headerSubtitleSuccess: 'We’ve received your message.',

      noSpam: 'No spam, unsubscribe at any time',
      cancel: 'Cancel',
      back: 'Back',
      continue: 'Continue',
      send: 'Send',
      close: 'Close',
      working: 'Working…',
      sending: 'Sending…',

      email: 'Email',
      emailPlaceholder: 'Enter your email',
      privacyRequired:
        'I accept the <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privacy policy</a> (required).',
      marketingOptional: 'I agree to receive relevant updates (optional).',

      firstName: 'First name',
      firstNamePlaceholder: 'Enter your first name',
      lastName: 'Last name',
      lastNamePlaceholder: 'Enter your last name',
      country: 'Country',
      countryPlaceholder: 'Select your country',
      selectCountry: 'Select a country…',
      phone: 'Phone',
      phonePlaceholder: '+44 7123 123456',
      phoneHelp: 'Include country code, for example +34 or +46.',
      hasWhatsApp: 'I have WhatsApp on this number.',
      availableOnWhatsapp: 'Available on WhatsApp',
      budget: 'Budget',
      buyTiming: 'When are you ready to buy',
      budget250_350: '€250k – €350k',
      budget350_500: '€350k – €500k',
      budget500_750: '€500k – €750k',
      budget750_1000: '€750k – €1M',
      budget1000_plus: '€1M+',
      timelineAsap: 'ASAP',
      timeline0_3: 'Within 3 months',
      timeline3_6: 'Within 6 months',
      timeline6_12: 'Within 12 months',
      ctaMatching: 'Get matching properties',
      footerPrivacy:
        'By continuing, you agree to our <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privacy policy</a>. We may send you relevant property updates. You can unsubscribe anytime.',
      requiredMark: '*',
      message: 'Message (optional)',
      messagePlaceholder: 'Tell us what you are interested in...',

      successTitle: 'Thank you for reaching out!',
      successMessage: 'We will contact you as soon as possible.',

      step1ValidationError:
        'Please enter a valid email address and accept the privacy policy to continue.',
      uspTitle: 'Fast & personal response',
      uspMessage:
        'Your request goes directly to the responsible team or real estate agent. We typically reply within 1 business day.',
      completeDetails: 'Almost There',
      completeDetailsDesc:
        'To provide you with personal service, we ask you to fill in your name and country',
    },
    errors: {
      required: 'This field is required',
      invalid_email: 'Please enter a valid email address',
      name_too_short: 'Name must be at least 2 characters',
      name_too_long: 'Name must not exceed 30 characters',
      invalid_name_format: 'Name can only contain letters, spaces, hyphens and apostrophes',
      invalid_name_special_chars:
        'Name cannot contain consecutive special characters or multiple spaces',
      invalid_name_numbers: 'Name cannot contain numbers',
      enter_phone: 'Please enter your phone number',
      valid_phone: 'Please enter a valid phone number',
      phone_country_code_required:
        'Please include the country code in your phone number, for example +34 or +46.',
      select_country: 'Please select a country',
      message_too_long: 'Message must not exceed 500 characters',
      privacy_required: 'You must accept the privacy policy to proceed.',
      select_budget: 'Please select a budget range.',
      select_timeline: 'Please select when you plan to buy.',
      send_failed: 'Failed to send. Please try again.',
    },
  },

  sv: {
    contactForm: {
      headerTitle: 'Kom i kontakt',
      headerTitleExpert: 'Matchas med en lokal expert',
      headerTitleSuccess: 'Tack!',
      headerSubtitleStep1: 'Ange din e-post så hör vi av oss inom kort.',
      headerSubtitleStep2: 'Fyll i några fler uppgifter så är du klar.',
      headerSubtitleExpert: 'Tar 30 sekunder • Ingen spam • Få relevanta objekt',
      headerSubtitleSuccess: 'Vi har tagit emot ditt meddelande.',

      noSpam: 'Ingen spam, avsluta prenumeration när som helst',
      cancel: 'Avbryt',
      back: 'Tillbaka',
      continue: 'Fortsätt',
      send: 'Skicka',
      close: 'Stäng',
      working: 'Arbetar…',
      sending: 'Skickar…',

      email: 'Email',
      emailPlaceholder: 'Ange din e-postadress',
      privacyRequired:
        'Jag accepterar <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">integritetspolicyn</a> (obligatoriskt).',
      marketingOptional: 'Jag godkänner att få relevanta uppdateringar (valfritt).',

      firstName: 'Förnamn',
      firstNamePlaceholder: 'Ange ditt förnamn',
      lastName: 'Efternamn',
      lastNamePlaceholder: 'Ange ditt efternamn',
      country: 'Land',
      countryPlaceholder: 'Välj ditt land',
      selectCountry: 'Välj land…',
      phone: 'Telefon',
      phonePlaceholder: 'Ange telefonnummer',
      phoneHelp: 'Inkludera landskod om möjligt.',
      hasWhatsApp: 'Jag har WhatsApp på detta nummer.',
      availableOnWhatsapp: 'Tillgänglig på WhatsApp',
      budget: 'Budget',
      buyTiming: 'När är du redo att köpa?',
      budget250_350: '250 000 € – 350 000 €',
      budget350_500: '350 000 € – 500 000 €',
      budget500_750: '500 000 € – 750 000 €',
      budget750_1000: '750 000 € – 1 M€',
      budget1000_plus: 'Över 1 M€',
      timelineAsap: 'Så snart som möjligt',
      timeline0_3: 'Inom 3 månader',
      timeline3_6: 'Inom 6 månader',
      timeline6_12: 'Inom 12 månader',
      ctaMatching: 'Få matchande objekt',
      footerPrivacy:
        'Genom att fortsätta godkänner du vår <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">integritetspolicy</a>. Vi kan skicka dig relevanta uppdateringar om bostäder. Du kan avsluta prenumerationen när som helst.',
      requiredMark: '*',
      message: 'Meddelande (valfritt)',
      messagePlaceholder: 'Berätta vad du är intresserad av...',

      successTitle: 'Tack för att du hörde av dig!',
      successMessage: 'Vi kontaktar dig så snart som möjligt.',
      step1ValidationError:
        'Ange en giltig e-postadress och acceptera integritetspolicyn för att fortsätta.',
      uspTitle: 'Snabb och personlig återkoppling',
      uspMessage:
        'Din förfrågan går direkt till ansvarigt team eller mäklare. Vi återkommer vanligtvis inom en arbetsdag.',
      completeDetails: 'Nästan klar',
      completeDetailsDesc:
        'För att kunna ge dig personlig service ber vi dig fylla i namn och land',
    },
    errors: {
      required: 'Detta fält är obligatoriskt',
      invalid_email: 'Ange en giltig e-postadress',
      name_too_short: 'Namnet måste vara minst 2 tecken',
      name_too_long: 'Namnet får inte överstiga 30 tecken',
      invalid_name_format:
        'Namnet kan bara innehålla bokstäver, mellanslag, bindestreck och apostrofer',
      invalid_name_special_chars:
        'Namnet får inte innehålla flera specialtecken i rad eller flera mellanslag',
      invalid_name_numbers: 'Namnet får inte innehålla siffror',
      enter_phone: 'Ange ditt telefonnummer',
      valid_phone: 'Ange ett giltigt telefonnummer',
      select_country: 'Välj ett land',
      message_too_long: 'Meddelandet får inte överstiga 500 tecken',
      privacy_required: 'Du måste acceptera integritetspolicyn för att fortsätta.',
      select_budget: 'Välj en budget.',
      select_timeline: 'Välj när du planerar att köpa.',
      send_failed: 'Misslyckades att skicka. Försök igen.',
    },
  },

  es: {
    contactForm: {
      headerTitle: 'Ponte en contacto',
      headerTitleExpert: 'Conecta con un experto local',
      headerTitleSuccess: '¡Gracias!',
      headerSubtitleStep1: 'Deja tu email y te responderemos pronto.',
      headerSubtitleStep2: 'Unos datos más y listo.',
      headerSubtitleExpert: 'Tarda 30 segundos • Sin spam • Recibe propiedades relevantes',
      headerSubtitleSuccess: 'Hemos recibido tu mensaje.',

      noSpam: 'Sin spam, darse de baja en cualquier momento',
      cancel: 'Cancelar',
      back: 'Atrás',
      continue: 'Continuar',
      send: 'Enviar',
      close: 'Cerrar',
      working: 'Procesando…',
      sending: 'Enviando…',

      email: 'Email',
      emailPlaceholder: 'Introduce tu dirección de correo electrónico',
      privacyRequired:
        'Acepto la <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">política de privacidad</a> (obligatorio).',
      marketingOptional: 'Acepto recibir actualizaciones relevantes (opcional).',

      firstName: 'Nombre',
      firstNamePlaceholder: 'Introduce tu nombre',
      lastName: 'Apellido',
      lastNamePlaceholder: 'Introduce tu apellido',
      country: 'País',
      countryPlaceholder: 'Selecciona tu país',
      selectCountry: 'Selecciona un país…',
      phone: 'Teléfono',
      phonePlaceholder: 'Introduce número de teléfono',
      phoneHelp: 'Incluye el prefijo del país si es posible.',
      hasWhatsApp: 'Tengo WhatsApp en este número.',
      availableOnWhatsapp: 'Disponible en WhatsApp',
      budget: 'Presupuesto',
      buyTiming: '¿Cuándo estarás listo para comprar?',
      budget250_350: '250.000 € – 350.000 €',
      budget350_500: '350.000 € – 500.000 €',
      budget500_750: '500.000 € – 750.000 €',
      budget750_1000: '750.000 € – 1 M€',
      budget1000_plus: 'Más de 1 M€',
      timelineAsap: 'Lo antes posible',
      timeline0_3: 'En un plazo de 3 meses',
      timeline3_6: 'En un plazo de 6 meses',
      timeline6_12: 'En un plazo de 12 meses',
      message: 'Mensaje (opcional)',
      messagePlaceholder: 'Cuéntanos qué te interesa...',

      successTitle: '¡Gracias por contactarnos!',
      successMessage: 'Nos pondremos en contacto contigo lo antes posible.',
      step1ValidationError:
        'Introduce una dirección de correo electrónico válida y acepta la política de privacidad para continuar.',
      uspTitle: 'Respuesta rápida y personal',
      uspMessage:
        'Tu solicitud se envía directamente al equipo responsable o al agente inmobiliario. Normalmente respondemos en un plazo de un día laborable.',
      completeDetails: 'Casi listo',
      completeDetailsDesc:
        'Para poder ofrecerte un servicio personal, te pedimos que rellenes tu nombre y país',
      ctaMatching: 'Recibir propiedades adecuadas',
      footerPrivacy:
        'Al continuar, aceptas nuestra <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">política de privacidad</a>. Podemos enviarte actualizaciones relevantes sobre propiedades. Puedes darte de baja en cualquier momento.',
      requiredMark: '*',
    },
    errors: {
      required: 'Este campo es obligatorio',
      invalid_email: 'Introduce una dirección de correo electrónico válida',
      name_too_short: 'El nombre debe tener al menos 2 caracteres',
      name_too_long: 'El nombre no debe superar 30 caracteres',
      invalid_name_format: 'El nombre solo puede contener letras, espacios, guiones y apóstrofes',
      invalid_name_special_chars:
        'El nombre no puede contener caracteres especiales consecutivos ni múltiples espacios',
      invalid_name_numbers: 'El nombre no puede contener números',
      enter_phone: 'Introduce tu número de teléfono',
      valid_phone: 'Introduce un número de teléfono válido',
      select_country: 'Selecciona un país',
      message_too_long: 'El mensaje no debe superar 500 caracteres',
      privacy_required: 'Debes aceptar la política de privacidad para continuar.',
      select_budget: 'Selecciona un presupuesto.',
      select_timeline: 'Selecciona cuándo planeas comprar.',
      send_failed: 'No se pudo enviar. Inténtalo de nuevo.',
    },
  },

  de: {
    contactForm: {
      headerTitle: 'In Kontakt kommen',
      headerTitleExpert: 'Mit einem lokalen Experten in Kontakt kommen',
      headerTitleSuccess: 'Vielen Dank!',
      headerSubtitleStep1: 'Hinterlassen Sie Ihre E-Mail und wir melden uns zeitnah.',
      headerSubtitleStep2: 'Nur noch ein paar Details – dann sind Sie fertig.',
      headerSubtitleExpert: 'Dauert 30 Sekunden • Kein Spam • Erhalten Sie passende Immobilien',
      headerSubtitleSuccess: 'Wir haben Ihre Nachricht erhalten.',

      noSpam: 'Kein Spam, jederzeit abbestellen',
      cancel: 'Abbrechen',
      back: 'Zurück',
      continue: 'Weiter',
      send: 'Senden',
      close: 'Schließen',
      working: 'Wird verarbeitet…',
      sending: 'Wird gesendet…',

      email: 'E-Mail',
      emailPlaceholder: 'Geben Sie Ihre E-Mail-Adresse ein',
      privacyRequired:
        'Ich akzeptiere die <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Datenschutzrichtlinie</a> (erforderlich).',
      marketingOptional: 'Ich stimme zu, relevante Updates zu erhalten (optional).',

      firstName: 'Vorname',
      firstNamePlaceholder: 'Geben Sie Ihren Vornamen ein',
      lastName: 'Nachname',
      lastNamePlaceholder: 'Geben Sie Ihren Nachnamen ein',
      country: 'Land',
      countryPlaceholder: 'Wählen Sie Ihr Land',
      selectCountry: 'Land auswählen…',
      phone: 'Telefon',
      phonePlaceholder: 'Telefonnummer eingeben',
      phoneHelp: 'Wenn möglich mit Ländervorwahl.',
      hasWhatsApp: 'Ich habe WhatsApp auf dieser Nummer.',
      availableOnWhatsapp: 'Auf WhatsApp erreichbar',
      budget: 'Budget',
      buyTiming: 'Wann sind Sie bereit zu kaufen?',
      budget250_350: '250.000 € – 350.000 €',
      budget350_500: '350.000 € – 500.000 €',
      budget500_750: '500.000 € – 750.000 €',
      budget750_1000: '750.000 € – 1 Mio. €',
      budget1000_plus: 'Über 1 Mio. €',
      timelineAsap: 'So schnell wie möglich',
      timeline0_3: 'Innerhalb von 3 Monaten',
      timeline3_6: 'Innerhalb von 6 Monaten',
      timeline6_12: 'Innerhalb von 12 Monaten',
      message: 'Nachricht (optional)',
      messagePlaceholder: 'Erzählen Sie uns, was Sie interessiert...',

      successTitle: 'Vielen Dank für Ihre Kontaktaufnahme!',
      successMessage: 'Wir werden uns so bald wie möglich bei Ihnen melden.',
      step1ValidationError:
        'Bitte geben Sie eine gültige E-Mail-Adresse ein und akzeptieren Sie die Datenschutzrichtlinie, um fortzufahren.',
      uspTitle: 'Schnelle & persönliche Rückmeldung',
      uspMessage:
        'Ihre Anfrage wird direkt an das zuständige Team oder den verantwortlichen Immobilienmakler weitergeleitet. In der Regel erhalten Sie innerhalb eines Werktages eine Antwort.',
      completeDetails: 'Fast fertig',
      completeDetailsDesc:
        'Um Ihnen einen persönlichen Service bieten zu können, bitten wir Sie, Ihren Namen und Ihr Land auszufüllen',
      ctaMatching: 'Passende Immobilien erhalten',
      footerPrivacy:
        'Wenn Sie fortfahren, stimmen Sie unserer <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Datenschutzrichtlinie</a> zu. Wir können Ihnen relevante Immobilien-Updates senden. Sie können sich jederzeit abmelden.',
      requiredMark: '*',
    },
    errors: {
      required: 'Dieses Feld ist erforderlich',
      invalid_email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      name_too_short: 'Der Name muss mindestens 2 Zeichen haben',
      name_too_long: 'Der Name darf 30 Zeichen nicht überschreiten',
      invalid_name_format:
        'Der Name darf nur Buchstaben, Leerzeichen, Bindestriche und Apostrophe enthalten',
      invalid_name_special_chars:
        'Der Name darf keine aufeinanderfolgenden Sonderzeichen oder mehrere Leerzeichen enthalten',
      invalid_name_numbers: 'Der Name darf keine Zahlen enthalten',
      enter_phone: 'Bitte geben Sie Ihre Telefonnummer ein',
      valid_phone: 'Bitte geben Sie eine gültige Telefonnummer ein',
      select_country: 'Bitte wählen Sie ein Land aus',
      message_too_long: 'Die Nachricht darf 500 Zeichen nicht überschreiten',
      privacy_required: 'Bitte akzeptieren Sie die Datenschutzrichtlinie, um fortzufahren.',
      select_budget: 'Bitte wählen Sie ein Budget aus.',
      select_timeline: 'Bitte wählen Sie, wann Sie kaufen möchten.',
      send_failed: 'Senden fehlgeschlagen. Bitte versuchen Sie es erneut.',
    },
  },

  fr: {
    contactForm: {
      headerTitle: 'Entrer en contact',
      headerTitleExpert: 'Entrez en relation avec un expert local',
      headerTitleSuccess: 'Merci !',
      headerSubtitleStep1: 'Laissez votre email et nous vous recontacterons rapidement.',
      headerSubtitleStep2: 'Encore quelques détails et c’est terminé.',
      headerSubtitleExpert: '30 secondes • Pas de spam • Recevez des biens pertinents',
      headerSubtitleSuccess: 'Nous avons reçu votre message.',

      noSpam: 'Pas de spam, se désabonner à tout moment',
      cancel: 'Annuler',
      back: 'Retour',
      continue: 'Continuer',
      send: 'Envoyer',
      close: 'Fermer',
      working: 'Traitement…',
      sending: 'Envoi…',

      email: 'Email',
      emailPlaceholder: 'Entrez votre adresse email',
      privacyRequired:
        'J\'accepte la <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">politique de confidentialité</a> (obligatoire).',
      marketingOptional: 'J’accepte de recevoir des mises à jour pertinentes (optionnel).',

      firstName: 'Prénom',
      firstNamePlaceholder: 'Entrez votre prénom',
      lastName: 'Nom de famille',
      lastNamePlaceholder: 'Entrez votre nom de famille',
      country: 'Pays',
      countryPlaceholder: 'Sélectionnez votre pays',
      selectCountry: 'Sélectionnez un pays…',
      phone: 'Téléphone',
      phonePlaceholder: 'Entrez le numéro de téléphone',
      phoneHelp: 'Incluez l’indicatif pays si possible.',
      hasWhatsApp: 'J’ai WhatsApp sur ce numéro.',
      availableOnWhatsapp: 'Disponible sur WhatsApp',
      budget: 'Budget',
      buyTiming: 'Quand serez-vous prêt à acheter ?',
      budget250_350: '250 000 € – 350 000 €',
      budget350_500: '350 000 € – 500 000 €',
      budget500_750: '500 000 € – 750 000 €',
      budget750_1000: '750 000 € – 1 M€',
      budget1000_plus: 'Plus d’1 M€',
      timelineAsap: 'Dès que possible',
      timeline0_3: 'Sous 3 mois',
      timeline3_6: 'Sous 6 mois',
      timeline6_12: 'Sous 12 mois',
      message: 'Message (optionnel)',
      messagePlaceholder: 'Dites-nous ce qui vous intéresse...',

      successTitle: 'Merci de nous avoir contactés !',
      successMessage: 'Nous vous contacterons dans les plus brefs délais.',
      step1ValidationError:
        'Veuillez saisir une adresse e-mail valide et accepter la politique de confidentialité pour continuer.',
      uspTitle: 'Réponse rapide et personnalisée',
      uspMessage:
        "Votre demande est transmise directement à l'équipe ou à l'agent immobilier responsable. Nous répondons généralement sous un jour ouvré.",
      completeDetails: 'Presque terminé',
      completeDetailsDesc:
        'Pour pouvoir vous offrir un service personnalisé, nous vous demandons de renseigner votre nom et votre pays',
      ctaMatching: 'Recevoir des biens correspondants',
      footerPrivacy:
        'En continuant, vous acceptez notre <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">politique de confidentialité</a>. Nous pouvons vous envoyer des mises à jour pertinentes sur les biens immobiliers. Vous pouvez vous désinscrire à tout moment.',
      requiredMark: '*',
    },
    errors: {
      required: 'Ce champ est obligatoire',
      invalid_email: 'Veuillez saisir une adresse e-mail valide',
      name_too_short: 'Le nom doit contenir au moins 2 caractères',
      name_too_long: 'Le nom ne doit pas dépasser 30 caractères',
      invalid_name_format:
        'Le nom ne peut contenir que des lettres, espaces, tirets et apostrophes',
      invalid_name_special_chars:
        'Le nom ne peut pas contenir de caractères spéciaux consécutifs ni plusieurs espaces',
      invalid_name_numbers: 'Le nom ne peut pas contenir de chiffres',
      enter_phone: 'Veuillez saisir votre numéro de téléphone',
      valid_phone: 'Veuillez saisir un numéro de téléphone valide',
      select_country: 'Veuillez sélectionner un pays',
      message_too_long: 'Le message ne doit pas dépasser 500 caractères',
      privacy_required: 'Vous devez accepter la politique de confidentialité pour continuer.',
      select_budget: 'Veuillez sélectionner un budget.',
      select_timeline: 'Veuillez sélectionner quand vous prévoyez d’acheter.',
      send_failed: 'Échec de l’envoi. Veuillez réessayer.',
    },
  },

  nl: {
    contactForm: {
      headerTitle: 'Neem contact op',
      headerTitleExpert: 'Kom in contact met een lokale expert',
      headerTitleSuccess: 'Bedankt!',
      headerSubtitleStep1: 'Laat je e-mail achter en we nemen snel contact op.',
      headerSubtitleStep2: 'Nog een paar gegevens en je bent klaar.',
      headerSubtitleExpert: 'Kost 30 seconden • Geen spam • Ontvang relevante woningen',
      headerSubtitleSuccess: 'We hebben je bericht ontvangen.',

      noSpam: 'Geen spam, uitschrijven op elk moment',
      cancel: 'Annuleren',
      back: 'Terug',
      continue: 'Doorgaan',
      send: 'Verzenden',
      close: 'Sluiten',
      working: 'Bezig…',
      sending: 'Verzenden…',

      email: 'E-mail',
      emailPlaceholder: 'Voer uw e-mailadres in',
      privacyRequired:
        'Ik accepteer het <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privacybeleid</a> (verplicht).',
      marketingOptional: 'Ik ga akkoord met relevante updates (optioneel).',

      firstName: 'Voornaam',
      firstNamePlaceholder: 'Voer uw voornaam in',
      lastName: 'Achternaam',
      lastNamePlaceholder: 'Voer uw achternaam in',
      country: 'Land',
      countryPlaceholder: 'Selecteer uw land',
      selectCountry: 'Selecteer een land…',
      phone: 'Telefoon',
      phonePlaceholder: 'Voer telefoonnummer in',
      phoneHelp: 'Voeg indien mogelijk landcode toe.',
      hasWhatsApp: 'Ik heb WhatsApp op dit nummer.',
      availableOnWhatsapp: 'Beschikbaar op WhatsApp',
      budget: 'Budget',
      buyTiming: 'Wanneer ben je klaar om te kopen?',
      budget250_350: '250.000 € – 350.000 €',
      budget350_500: '350.000 € – 500.000 €',
      budget500_750: '500.000 € – 750.000 €',
      budget750_1000: '750.000 € – 1 mln. €',
      budget1000_plus: 'Meer dan 1 mln. €',
      timelineAsap: 'Zo snel mogelijk',
      timeline0_3: 'Binnen 3 maanden',
      timeline3_6: 'Binnen 6 maanden',
      timeline6_12: 'Binnen 12 maanden',
      message: 'Bericht (optioneel)',
      messagePlaceholder: 'Vertel ons waar u in geïnteresseerd bent...',

      successTitle: 'Bedankt voor uw bericht!',
      successMessage: 'We nemen zo spoedig mogelijk contact met u op.',
      step1ValidationError:
        'Voer een geldig e-mailadres in en accepteer het privacybeleid om door te gaan.',
      uspTitle: 'Snelle en persoonlijke reactie',
      uspMessage:
        'Uw aanvraag wordt rechtstreeks doorgestuurd naar het verantwoordelijke team of de makelaar. Wij reageren doorgaans binnen één werkdag.',
      completeDetails: 'Bijna klaar',
      completeDetailsDesc:
        'Om u persoonlijke service te kunnen bieden, vragen wij u naam en land in te vullen',
      ctaMatching: 'Ontvang passende woningen',
      footerPrivacy:
        'Door verder te gaan, ga je akkoord met ons <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privacybeleid</a>. We kunnen je relevante woningupdates sturen. Je kunt je op elk moment uitschrijven.',
      requiredMark: '*',
    },
    errors: {
      required: 'Dit veld is verplicht',
      invalid_email: 'Voer een geldig e-mailadres in',
      name_too_short: 'De naam moet minimaal 2 tekens bevatten',
      name_too_long: 'De naam mag niet meer dan 30 tekens bevatten',
      invalid_name_format:
        'De naam mag alleen letters, spaties, koppeltekens en apostrofen bevatten',
      invalid_name_special_chars:
        'De naam mag geen opeenvolgende speciale tekens of meerdere spaties bevatten',
      invalid_name_numbers: 'De naam mag geen cijfers bevatten',
      enter_phone: 'Voer uw telefoonnummer in',
      valid_phone: 'Voer een geldig telefoonnummer in',
      select_country: 'Selecteer een land',
      message_too_long: 'Het bericht mag niet meer dan 500 tekens bevatten',
      privacy_required: 'U moet het privacybeleid accepteren om door te gaan.',
      select_budget: 'Selecteer een budget.',
      select_timeline: 'Selecteer wanneer je van plan bent te kopen.',
      send_failed: 'Verzenden mislukt. Probeer het opnieuw.',
    },
  },

  fi: {
    contactForm: {
      headerTitle: 'Ota yhteyttä',
      headerTitleExpert: 'Löydä paikallinen asiantuntija',
      headerTitleSuccess: 'Kiitos!',
      headerSubtitleStep1: 'Jätä sähköpostisi niin otamme pian yhteyttä.',
      headerSubtitleStep2: 'Täytä vielä muutama tieto, niin olet valmis.',
      headerSubtitleExpert: 'Vie 30 sekuntia • Ei roskapostia • Saat sinulle sopivia kohteita',
      headerSubtitleSuccess: 'Olemme vastaanottaneet viestisi.',

      noSpam: 'Ei roskapostia, peruuta tilaus milloin tahansa',
      cancel: 'Peruuta',
      back: 'Takaisin',
      continue: 'Jatka',
      send: 'Lähetä',
      close: 'Sulje',
      working: 'Käsitellään…',
      sending: 'Lähetetään…',

      email: 'Sähköposti',
      emailPlaceholder: 'Syötä sähköpostiosoitteesi',
      privacyRequired:
        'Hyväksyn <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">tietosuojakäytännön</a> (pakollinen).',
      marketingOptional: 'Hyväksyn relevantit päivitykset (valinnainen).',

      firstName: 'Etunimi',
      firstNamePlaceholder: 'Syötä etunimesi',
      lastName: 'Sukunimi',
      lastNamePlaceholder: 'Syötä sukunimesi',
      country: 'Maa',
      countryPlaceholder: 'Valitse maasi',
      selectCountry: 'Valitse maa…',
      phone: 'Puhelin',
      phonePlaceholder: 'Syötä puhelinnumero',
      phoneHelp: 'Lisää maakoodi jos mahdollista.',
      hasWhatsApp: 'Minulla on WhatsApp tässä numerossa.',
      availableOnWhatsapp: 'Tavoitettavissa WhatsAppissa',
      budget: 'Budjetti',
      buyTiming: 'Milloin olet valmis ostamaan?',
      budget250_350: '250 000 € – 350 000 €',
      budget350_500: '350 000 € – 500 000 €',
      budget500_750: '500 000 € – 750 000 €',
      budget750_1000: '750 000 € – 1 M€',
      budget1000_plus: 'Yli 1 M€',
      timelineAsap: 'Mahdollisimman pian',
      timeline0_3: '3 kuukauden sisällä',
      timeline3_6: '6 kuukauden sisällä',
      timeline6_12: '12 kuukauden sisällä',
      message: 'Viesti (valinnainen)',
      messagePlaceholder: 'Kerro, mikä kiinnostaa sinua...',

      successTitle: 'Kiitos yhteydenotostasi!',
      successMessage: 'Otammekin yhteyttä mahdollisimman pian.',
      step1ValidationError:
        'Syötä voimassa oleva sähköpostiosoite ja hyväksy tietosuojakäytäntö jatkaaksesi.',
      uspTitle: 'Nopea ja henkilökohtainen vastaus',
      uspMessage:
        'Pyyntösi ohjataan suoraan vastuulliselle tiimille tai kiinteistönvälittäjälle. Vastaamme yleensä yhden arkipäivän kuluessa.',
      completeDetails: 'Melkein valmis',
      completeDetailsDesc:
        'Voidaksemme tarjota sinulle henkilökohtaista palvelua pyydämme sinua täyttämään nimen ja maan',
      ctaMatching: 'Saa sopivia kohteita',
      footerPrivacy:
        'Jatkamalla hyväksyt <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">tietosuojakäytäntömme</a>. Saatamme lähettää sinulle relevantteja kiinteistöpäivityksiä. Voit peruuttaa tilauksen milloin tahansa.',
      requiredMark: '*',
    },
    errors: {
      required: 'Tämä kenttä on pakollinen',
      invalid_email: 'Syötä kelvollinen sähköpostiosoite',
      name_too_short: 'Nimen tulee olla vähintään 2 merkkiä',
      name_too_long: 'Nimen tulee olla enintään 30 merkkiä',
      invalid_name_format:
        'Nimi voi sisältää vain kirjaimia, välilyöntejä, väliviivoja ja heittomerkkejä',
      invalid_name_special_chars:
        'Nimi ei voi sisältää peräkkäisiä erikoismerkkejä tai useita välilyöntejä',
      invalid_name_numbers: 'Nimi ei voi sisältää numeroita',
      enter_phone: 'Syötä puhelinnumerosi',
      valid_phone: 'Syötä kelvollinen puhelinnumero',
      select_country: 'Valitse maa',
      message_too_long: 'Viestin tulee olla enintään 500 merkkiä',
      privacy_required: 'Sinun täytyy hyväksyä tietosuojakäytäntö jatkaaksesi.',
      select_budget: 'Valitse budjetti.',
      select_timeline: 'Valitse milloin aiot ostaa.',
      send_failed: 'Lähetys epäonnistui. Yritä uudelleen.',
    },
  },

  no: {
    contactForm: {
      headerTitle: 'Kom i kontakt',
      headerTitleExpert: 'Bli matchet med en lokal ekspert',
      headerTitleSuccess: 'Takk!',
      headerSubtitleStep1: 'Legg igjen e-posten din så tar vi kontakt snart.',
      headerSubtitleStep2: 'Noen få detaljer til, så er du ferdig.',
      headerSubtitleExpert: 'Tar 30 sekunder • Ingen spam • Få relevante boliger',
      headerSubtitleSuccess: 'Vi har mottatt meldingen din.',

      noSpam: 'Ingen spam, avslutt abonnement når som helst',
      cancel: 'Avbryt',
      back: 'Tilbake',
      continue: 'Fortsett',
      send: 'Send',
      close: 'Lukk',
      working: 'Jobber…',
      sending: 'Sender…',

      email: 'E-post',
      emailPlaceholder: 'Skriv inn din e-postadresse',
      privacyRequired:
        'Jeg godtar <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">personvernerklæringen</a> (påkrevd).',
      marketingOptional: 'Jeg samtykker til relevante oppdateringer (valgfritt).',

      firstName: 'Fornavn',
      firstNamePlaceholder: 'Skriv inn fornavn',
      lastName: 'Etternavn',
      lastNamePlaceholder: 'Skriv inn etternavn',
      country: 'Land',
      countryPlaceholder: 'Velg ditt land',
      selectCountry: 'Velg land…',
      phone: 'Telefon',
      phonePlaceholder: 'Skriv inn telefonnummer',
      phoneHelp: 'Inkluder landskode hvis mulig.',
      hasWhatsApp: 'Jeg har WhatsApp på dette nummeret.',
      availableOnWhatsapp: 'Tilgjengelig på WhatsApp',
      budget: 'Budsjett',
      buyTiming: 'Når er du klar til å kjøpe?',
      budget250_350: '250 000 € – 350 000 €',
      budget350_500: '350 000 € – 500 000 €',
      budget500_750: '500 000 € – 750 000 €',
      budget750_1000: '750 000 € – 1 M€',
      budget1000_plus: 'Over 1 M€',
      timelineAsap: 'Så snart som mulig',
      timeline0_3: 'Innen 3 måneder',
      timeline3_6: 'Innen 6 måneder',
      timeline6_12: 'Innen 12 måneder',
      message: 'Melding (valgfritt)',
      messagePlaceholder: 'Fortell oss hva du er interessert i...',

      successTitle: 'Takk for at du tok kontakt!',
      successMessage: 'Vi kontakter deg så snart som mulig.',
      step1ValidationError:
        'Skriv inn en gyldig e-postadresse og godta personvernerklæringen for å fortsette.',
      uspTitle: 'Rask og personlig respons',
      uspMessage:
        'Forespørselen din går direkte til ansvarlig team eller eiendomsmegler. Vi svarer vanligvis innen én virkedag.',
      completeDetails: 'Nesten ferdig',
      completeDetailsDesc: 'For å kunne gi deg personlig service ber vi deg fylle ut navn og land',
      ctaMatching: 'Få matchende boliger',
      footerPrivacy:
        'Ved å fortsette godtar du vår <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">personvernerklæring</a>. Vi kan sende deg relevante boligoppdateringer. Du kan melde deg av når som helst.',
      requiredMark: '*',
    },
    errors: {
      required: 'Dette feltet er påkrevd',
      invalid_email: 'Skriv inn en gyldig e-postadresse',
      name_too_short: 'Navnet må være minst 2 tegn',
      name_too_long: 'Navnet må ikke overstige 30 tegn',
      invalid_name_format:
        'Navnet kan bare inneholde bokstaver, mellomrom, bindestrek og apostrofer',
      invalid_name_special_chars:
        'Navnet kan ikke inneholde påfølgende spesialtegn eller flere mellomrom',
      invalid_name_numbers: 'Navnet kan ikke inneholde tall',
      enter_phone: 'Skriv inn telefonnummeret ditt',
      valid_phone: 'Skriv inn et gyldig telefonnummer',
      select_country: 'Velg land',
      message_too_long: 'Meldingen må ikke overstige 500 tegn',
      privacy_required: 'Du må godta personvernerklæringen for å fortsette.',
      select_budget: 'Velg et budsjett.',
      select_timeline: 'Velg når du planlegger å kjøpe.',
      send_failed: 'Kunne ikke sende. Prøv igjen.',
    },
  },

  da: {
    contactForm: {
      headerTitle: 'Kom i kontakt',
      headerTitleExpert: 'Bliv matchet med en lokal ekspert',
      headerTitleSuccess: 'Tak!',
      headerSubtitleStep1: 'Efterlad din e-mail, så vender vi hurtigt tilbage.',
      headerSubtitleStep2: 'Et par detaljer mere, og så er du færdig.',
      headerSubtitleExpert: 'Tager 30 sekunder • Ingen spam • Få relevante boliger',
      headerSubtitleSuccess: 'Vi har modtaget din besked.',

      noSpam: 'Ingen spam, afmeld når som helst',
      cancel: 'Annuller',
      back: 'Tilbage',
      continue: 'Fortsæt',
      send: 'Send',
      close: 'Luk',
      working: 'Arbejder…',
      sending: 'Sender…',

      email: 'E-mail',
      emailPlaceholder: 'Indtast din e-mailadresse',
      privacyRequired:
        'Jeg accepterer <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privatlivspolitikken</a> (påkrævet).',
      marketingOptional: 'Jeg accepterer relevante opdateringer (valgfrit).',

      firstName: 'Fornavn',
      firstNamePlaceholder: 'Indtast dit fornavn',
      lastName: 'Efternavn',
      lastNamePlaceholder: 'Indtast dit efternavn',
      country: 'Land',
      countryPlaceholder: 'Vælg dit land',
      selectCountry: 'Vælg land…',
      phone: 'Telefon',
      phonePlaceholder: 'Indtast telefonnummer',
      phoneHelp: 'Inkludér landekode hvis muligt.',
      hasWhatsApp: 'Jeg har WhatsApp på dette nummer.',
      availableOnWhatsapp: 'Tilgængelig på WhatsApp',
      budget: 'Budget',
      buyTiming: 'Hvornår er du klar til at købe?',
      budget250_350: '250.000 € – 350.000 €',
      budget350_500: '350.000 € – 500.000 €',
      budget500_750: '500.000 € – 750.000 €',
      budget750_1000: '750.000 € – 1 mio. €',
      budget1000_plus: 'Over 1 mio. €',
      timelineAsap: 'Så hurtigt som muligt',
      timeline0_3: 'Inden for 3 måneder',
      timeline3_6: 'Inden for 6 måneder',
      timeline6_12: 'Inden for 12 måneder',
      message: 'Besked (valgfrit)',
      messagePlaceholder: 'Fortæl os, hvad du er interesseret i...',

      successTitle: 'Tak for din henvendelse!',
      successMessage: 'Vi kontakter dig så hurtigt som muligt.',
      step1ValidationError:
        'Indtast en gyldig e-mailadresse og accepter privatlivspolitikken for at fortsætte.',
      uspTitle: 'Hurtigt & personligt svar',
      uspMessage:
        'Din henvendelse sendes direkte til det ansvarlige team eller ejendomsmægler. Vi svarer som regel inden for én hverdag.',
      completeDetails: 'Næsten færdig',
      completeDetailsDesc:
        'For at kunne give dig personlig service beder vi dig udfylde navn og land',
      ctaMatching: 'Få matchende boliger',
      footerPrivacy:
        'Ved at fortsætte accepterer du vores <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">privatlivspolitik</a>. Vi kan sende dig relevante boligopdateringer. Du kan afmelde dig når som helst.',
      requiredMark: '*',
    },
    errors: {
      required: 'Dette felt er påkrævet',
      invalid_email: 'Indtast en gyldig e-mailadresse',
      name_too_short: 'Navnet skal være mindst 2 tegn',
      name_too_long: 'Navnet må ikke overstige 30 tegn',
      invalid_name_format:
        'Navnet må kun indeholde bogstaver, mellemrum, bindestreger og apostroffer',
      invalid_name_special_chars:
        'Navnet må ikke indeholde på hinanden følgende specialtegn eller flere mellemrum',
      invalid_name_numbers: 'Navnet må ikke indeholde tal',
      enter_phone: 'Indtast dit telefonnummer',
      valid_phone: 'Indtast et gyldigt telefonnummer',
      select_country: 'Vælg land',
      message_too_long: 'Beskeden må ikke overstige 500 tegn',
      privacy_required: 'Du skal acceptere privatlivspolitikken for at fortsætte.',
      select_budget: 'Vælg et budget.',
      select_timeline: 'Vælg hvornår du planlægger at købe.',
      send_failed: 'Kunne ikke sende. Prøv igen.',
    },
  },

  pl: {
    contactForm: {
      headerTitle: 'Skontaktuj się z nami',
      headerTitleExpert: 'Połącz się z lokalnym ekspertem',
      headerTitleSuccess: 'Dziękujemy!',
      headerSubtitleStep1: 'Zostaw swój email, a odezwiemy się szybko.',
      headerSubtitleStep2: 'Jeszcze kilka danych i gotowe.',
      headerSubtitleExpert: 'Zajmuje 30 sekund • Bez spamu • Otrzymuj dopasowane oferty',
      headerSubtitleSuccess: 'Otrzymaliśmy Twoją wiadomość.',

      noSpam: 'Bez spamu, możesz zrezygnować w dowolnym momencie',
      cancel: 'Anuluj',
      back: 'Wstecz',
      continue: 'Kontynuuj',
      send: 'Wyślij',
      close: 'Zamknij',
      working: 'Przetwarzanie…',
      sending: 'Wysyłanie…',

      email: 'Email',
      emailPlaceholder: 'Wprowadź swój adres email',
      privacyRequired:
        'Akceptuję <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">politykę prywatności</a> (wymagane).',
      marketingOptional: 'Zgadzam się na otrzymywanie istotnych aktualizacji (opcjonalnie).',

      firstName: 'Imię',
      firstNamePlaceholder: 'Wprowadź swoje imię',
      lastName: 'Nazwisko',
      lastNamePlaceholder: 'Wprowadź swoje nazwisko',
      country: 'Kraj',
      countryPlaceholder: 'Wybierz swój kraj',
      selectCountry: 'Wybierz kraj…',
      phone: 'Telefon',
      phonePlaceholder: 'Wprowadź numer telefonu',
      phoneHelp: 'Jeśli to możliwe, podaj prefiks kraju.',
      hasWhatsApp: 'Mam WhatsApp na tym numerze.',
      availableOnWhatsapp: 'Dostępny na WhatsAppie',
      budget: 'Budżet',
      buyTiming: 'Kiedy będziesz gotowy do zakupu?',
      budget250_350: '250 000 € – 350 000 €',
      budget350_500: '350 000 € – 500 000 €',
      budget500_750: '500 000 € – 750 000 €',
      budget750_1000: '750 000 € – 1 mln €',
      budget1000_plus: 'Ponad 1 mln €',
      timelineAsap: 'Jak najszybciej',
      timeline0_3: 'W ciągu 3 miesięcy',
      timeline3_6: 'W ciągu 6 miesięcy',
      timeline6_12: 'W ciągu 12 miesięcy',
      message: 'Wiadomość (opcjonalnie)',
      messagePlaceholder: 'Powiedz nam, co Cię interesuje...',

      successTitle: 'Dziękujemy za kontakt!',
      successMessage: 'Skontaktujemy się z Tobą tak szybko, jak to możliwe.',
      step1ValidationError:
        'Wprowadź poprawny adres email i zaakceptuj politykę prywatności, aby kontynuować.',
      uspTitle: 'Szybka i osobista odpowiedź',
      uspMessage:
        'Twoje zapytanie trafia bezpośrednio do odpowiedniego zespołu lub agenta nieruchomości. Zazwyczaj odpowiadamy w ciągu jednego dnia roboczego.',
      completeDetails: 'Prawie gotowe',
      completeDetailsDesc:
        'Aby móc zaoferować Ci osobistą obsługę, prosimy o podanie imienia i kraju',
      ctaMatching: 'Pokaż dopasowane oferty',
      footerPrivacy:
        'Kontynuując, akceptujesz naszą <a href="https://bostello.com/privacy-policy/" target="_blank" rel="noopener noreferrer">politykę prywatności</a>. Możemy wysyłać Ci istotne aktualizacje dotyczące nieruchomości. Możesz zrezygnować w dowolnym momencie.',
      requiredMark: '*',
    },
    errors: {
      required: 'To pole jest wymagane',
      invalid_email: 'Wprowadź poprawny adres email',
      name_too_short: 'Imię musi mieć co najmniej 2 znaki',
      name_too_long: 'Imię nie może przekraczać 30 znaków',
      invalid_name_format: 'Imię może zawierać tylko litery, spacje, myślniki i apostrofy',
      invalid_name_special_chars:
        'Imię nie może zawierać kolejnych znaków specjalnych ani wielu spacji',
      invalid_name_numbers: 'Imię nie może zawierać cyfr',
      enter_phone: 'Wprowadź numer telefonu',
      valid_phone: 'Wprowadź poprawny numer telefonu',
      select_country: 'Wybierz kraj',
      message_too_long: 'Wiadomość nie może przekraczać 500 znaków',
      privacy_required: 'Musisz zaakceptować politykę prywatności, aby kontynuować.',
      select_budget: 'Wybierz budżet.',
      select_timeline: 'Wybierz, kiedy planujesz kupić.',
      send_failed: 'Nie udało się wysłać. Spróbuj ponownie.',
    },
  },
}
